import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton, Grid, Divider, TableCell, TableRow, Table, TableBody } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageProductTable from '../tabels/StageProductTable';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey, red, green } from '@mui/material/colors';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import theme from 'assets/theme';
import ProductList from './ProductList';

import { useVisibility } from 'context/visibilityContext'; // Import the hook
import { filterDiffsByDisplayColumns } from 'utils/visibilityFilter'; // Import the filter utility
import DiffRenderer from './DiffRenderer';

const StyledTableRow = styled(TableRow)(({ theme, selected }) => ({
  transition: 'background-color 0.3s ease',
  cursor: 'pointer',
  '&:hover > *': {
    backgroundColor: theme.palette.action.hover,
  },
  ...(selected && {
    '& > *': {
      backgroundColor: theme.palette.action.selected,
    },
  }),
}));

function PricebooksReview({ selectedOrg, productData, showSameFields }) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [productDataMap, setProductDataMap] = useState({});
  const [expandedPriceEntries, setExpandedPriceEntries] = useState({});
  const [selectedProductId, setSelectedProductId] = useState(null);

  const { visibilityConfig, isLoadingConfig, configError } = useVisibility(); // Get context values

  const pricebookColumns = [
    { name: 'pricebook', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];
  const priceEntryColumns = [
    { name: 'priceEntry', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  useEffect(() => {
    if (!productData || productData.length === 0) {
      setProductDataMap({});
      return;
    }

    const generatedProductDataMap = {};

    productData.forEach((productDiff) => {
      // Maintain the categorized structure: new, changed, deleted, same
      const generatedPricebookRows = { new: [], changed: [], deleted: [], same: [] };

      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (productDiff.pricebooks[type] || []).forEach((pricebook) => {
          const pricebookData = {
            id: pricebook.sfId || pricebook.id,
            pricebook: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {pricebook.name || pricebook.Name || pricebook.fields?.Name || pricebook.sfId}
              </ArgonTypography>
            ),
            sObjectName: 'Pricebook2', // <--- ADD SOBJECTNAME FOR PRICEBOOK

            diffs: pricebook.changes,
            priceEntries: { new: [], changed: [], deleted: [], same: [] },
            type: pricebook.type, // Keep the type
          };

          if (pricebook.priceEntries) {
            ['new', 'changed', 'deleted', 'same'].forEach((subType) => {
              (pricebook.priceEntries[subType] || []).forEach((priceEntry) => {
                pricebookData.priceEntries[subType].push({
                  id: priceEntry.sfId || priceEntry.id,
                  priceEntry: (
                    <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                      {priceEntry.fields?.UnitPrice + ' ' + (priceEntry.fields?.CurrencyIsoCode || 'EUR') ||
                        priceEntry.id}
                    </ArgonTypography>
                  ),
                  sObjectName: 'PricebookEntry', // <--- ADD SOBJECTNAME FOR PRICE ENTRY

                  diffs: priceEntry.changes,
                  type: subType, // Keep the type
                });
              });
            });
          }
          generatedPricebookRows[type].push(pricebookData);
        });
      });

      generatedProductDataMap[productDiff.product.sfId || productDiff.product.id || productDiff.product.external_id] = {
        pricebookRows: generatedPricebookRows, // Categorized structure
        productName: productDiff.product.name,
      };
    });

    setProductDataMap(generatedProductDataMap);
    if (productData.length > 0) {
      setSelectedProductId(
        productData[0].product.sfId || productData[0].product.id || productData[0].product.external_id
      );
    }
  }, [productData]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prevExpandedRow) => (prevExpandedRow === rowId ? null : rowId));
  };

  const handlePriceEntryClick = (priceEntryId) => {
    setExpandedPriceEntries((prevExpandedPriceEntries) => ({
      ...prevExpandedPriceEntries,
      [priceEntryId]: !prevExpandedPriceEntries[priceEntryId],
    }));
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
  };

  const renderExpandedContent = (row) => {
    return (
      <TableRow style={{ width: '100%', padding: '0px' }}>
        <TableCell colSpan={pricebookColumns.length} style={{ padding: '0px' }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit style={{ width: '100%' }}>
            <ArgonBox p={3} color="text" bgcolor={grey[50]}>
              <DiffRenderer
                changes={row.diffs}
                sObjectName={row.sObjectName}
                orgType={selectedOrg.type.toLowerCase()}
                showSameFields={showSameFields}
              />
              {row.priceEntries &&
                (row.priceEntries.new.length > 0 ||
                  row.priceEntries.changed.length > 0 ||
                  row.priceEntries.deleted.length > 0 ||
                  row.priceEntries.same.length > 0) && (
                  <ArgonBox mt={2}>
                    <Table>
                      <TableBody>
                        {['new', 'changed', 'deleted', 'same'].map(
                          (type) =>
                            row.priceEntries[type].length > 0 && (
                              <React.Fragment key={type}>
                                <ArgonTypography variant="h6" fontWeight="medium" mb={1}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)} Price Entries
                                </ArgonTypography>
                                {row.priceEntries[type].map((priceEntry) => (
                                  <StyledTableRow key={priceEntry.id} selected={expandedPriceEntries[priceEntry.id]}>
                                    {renderPriceEntryRow(priceEntry)}
                                  </StyledTableRow>
                                ))}
                              </React.Fragment>
                            )
                        )}
                      </TableBody>
                    </Table>
                  </ArgonBox>
                )}
            </ArgonBox>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderPriceEntryRow = (priceEntry) => {
    return (
      <TableCell style={{ padding: '0px', width: '100%' }}>
        <ArgonBox
          display="flex"
          alignItems="center"
          onClick={() => handlePriceEntryClick(priceEntry.id)}
          style={{ cursor: 'pointer' }}
          p={1}
        >
          <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
            {priceEntry.priceEntry}
          </ArgonTypography>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handlePriceEntryClick(priceEntry.id);
            }}
          >
            <Icon>{expandedPriceEntries[priceEntry.id] ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        </ArgonBox>
        <Collapse in={expandedPriceEntries[priceEntry.id]} timeout="auto" unmountOnExit>
          <ArgonBox p={2} bgcolor={grey[100]}>
            <DiffRenderer
              changes={priceEntry.diffs}
              sObjectName={priceEntry.sObjectName}
              orgType={selectedOrg.type.toLowerCase()}
              showSameFields={showSameFields}
            />
          </ArgonBox>
        </Collapse>
      </TableCell>
    );
  };

  const renderProductSection = (productName, productData) => {
    const { pricebookRows } = productData;

    const renderTableSection = (title, rows, columns, renderRowSubComponent) => {
      if (!rows || rows.length === 0) {
        return null; // Don't render anything if there are no rows at all
      }
      return (
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                {title}
              </ArgonTypography>
              <ArgonBox
                sx={{
                  '& .MuiTableRow-root:not(:last-child)': {
                    '& td': {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {renderRowSubComponent ? (
                  <StageExpandableTable
                    rows={rows}
                    columns={columns}
                    renderRowSubComponent={({ row }) => renderRowSubComponent(row)}
                  />
                ) : (
                  <StageProductTable rows={rows} columns={columns} />
                )}
              </ArgonBox>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    };

    const formattedRows = (rows) => {
      return rows.map((row) => ({
        ...row,
        renderExpandedContent: () => renderExpandedContent(row),
        onClick: () => handleRowClick(row.id),
        selected: expandedRow === row.id,
        actions: (
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.id);
            }}
          >
            <Icon>{expandedRow === row.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        ),
      }));
    };

    const hasPricebookData = ['new', 'changed', 'deleted', 'same'].some((type) => pricebookRows[type].length > 0);

    if (!hasPricebookData) {
      return (
        <ArgonBox key={productName} mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                No pricebooks data available for this product.
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    }

    return (
      <ArgonBox key={productName} mb={3}>
        {/* Iterate over the categories, just like in ComponentsReview */}
        {['new', 'changed', 'deleted', 'same'].map(
          (type) =>
            // Conditionally render the *entire section*
            pricebookRows[type].length > 0 && (
              <React.Fragment key={type}>
                {renderTableSection(
                  `${type.charAt(0).toUpperCase() + type.slice(1)} Pricebooks`,
                  formattedRows(pricebookRows[type]), // Pass the categorized rows
                  pricebookColumns,
                  (row) => row.renderExpandedContent()
                )}
              </React.Fragment>
            )
        )}
      </ArgonBox>
    );
  };

  const productList = Object.keys(productDataMap).map((productId) => ({
    id: productId,
    name: productDataMap[productId].productName,
  }));

  return (
    <ArgonBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProductList
            products={productList}
            selectedProductId={selectedProductId}
            onProductSelect={handleProductSelect}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedProductId && productDataMap[selectedProductId] ? (
            renderProductSection(productDataMap[selectedProductId].productName, productDataMap[selectedProductId])
          ) : (
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="body1" color="textSecondary" sx={{ fontSize: '12pt', textAlign: 'center' }}>
                  Select a product to view its pricebooks.
                </ArgonTypography>
              </ArgonBox>
            </Card>
          )}
        </Grid>
      </Grid>
    </ArgonBox>
  );
}

PricebooksReview.propTypes = {
  productData: PropTypes.arrayOf(
    PropTypes.shape({
      pricebooks: PropTypes.shape({
        new: PropTypes.arrayOf(PropTypes.object).isRequired,
        changed: PropTypes.arrayOf(PropTypes.object).isRequired,
        deleted: PropTypes.arrayOf(PropTypes.object).isRequired,
        same: PropTypes.arrayOf(PropTypes.object).isRequired,
      }).isRequired,
      product: PropTypes.shape({
        name: PropTypes.string,
        sf_id: PropTypes.string,
        external_id: PropTypes.string,
        id: PropTypes.string,
      }),
    })
  ).isRequired,
  showSameFields: PropTypes.bool, // Add showSameFields prop
};

export default PricebooksReview;
